import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/iconify';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import ModifiedDialog from '../dialogs/ModifiedDialog';
import config from '../../config';
import { modifiedReasonConfig } from '../../config/modifiedReasonConfig';
import { useStateSelector } from '../../context/StateSelectors';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import { MAIN_CONTENT_OFFSET } from '../../layouts/dashboard/MainLayout';
import { isAppleDevice } from '../../components/command-palette/useCommandPalette';
import { adaptedName } from '../../utils/dataHelpers';
import { formatApiErrors, hasError } from '../../utils/formatError';
import { findMenuTrail } from '../../utils/menuTrail';
import { fDateTime } from '../../utils/formatTime';
import { isValidScenarioName, parseJsonObjectText, toJsonText } from '../../utils/scraperScenarios';
import i18n from '../../i18n';
import CodeEditor from './CodeEditor';
import ScenarioPreviewPanel from './ScenarioPreviewPanel';
import { DEBUGGER_EDITOR_OPTIONS, ScenarioDebuggerStyles, createScenarioDebugger } from './scenarioDebugger';
import { installScenarioSandbox } from './scenarioSandbox';
import { SCRAPER_SCENARIOS_PATH, scenarioEditorPath } from './scenarioPaths';

const NAME = 'publicScraperScenarios';
const RUN_ACTION = 'scraper-scenario-run';

// What a new scenario starts with: a working script, and the conventions spelled
// out where they are needed.
const STARTER_CODE = `// The body of an async function: await and return work at the top level.
// In scope: page, browser, input, proxy, setText, setImage, setResult, log, Buffer.
await page.goto(input.url || 'https://example.com');
log('opened', page.url());
return await page.title();
`;

const EMPTY = { name: '', description: '', code: STARTER_CODE, inputTemplateText: '{}', isArchived: false };

/** The editable part of a scenario, as the page holds it. */
const fromEntity = (entity) => ({
    name: entity.name || '',
    description: entity.description || '',
    code: entity.code || '',
    inputTemplateText: toJsonText(entity.inputTemplate),
    isArchived: Boolean(entity.isArchived)
});

const sameDraft = (a, b) =>
    a.name === b.name &&
    a.description === b.description &&
    a.code === b.code &&
    a.inputTemplateText === b.inputTemplateText &&
    a.isArchived === b.isArchived;

const TEMPLATE_ERRORS = {
    invalidJson: 'scraperScenarios.templateInvalid',
    notObject: 'scraperScenarios.templateNotObject'
};

/**
 * One scraper scenario: its code in a real editor, and a preview run against the
 * remote browser beside it.
 *
 * Saving is live. Gateway settings reference a scenario rather than copy it, so
 * the code saved here is what the next payin or payout of every gateway linking
 * them runs — which is why the settings that use it are listed at the top, with a
 * warning.
 */
function ScenarioEditor({ routeId, editorKey, context, navigate, setHeaderActions, clearHeaderActions, onCreated }) {
    const server = context.getState().server;
    const scenariosServer = server.publicScraperScenarios;

    const permissions = useStateSelector((state) => state.permissions);
    const actionPermissions = useStateSelector((state) => state.actions);
    const permissionsLoaded = Object.keys(permissions || {}).length > 0;
    // The same reading GridPageAbstract.allowedPermission gives them.
    const allowed = (verb, controller = NAME) =>
        permissionsLoaded && permissions?.[adaptedName(controller)]?.[verb] !== 'deny';

    const isNewRoute = routeId === 'new';
    const routeValid = isNewRoute || /^\d+$/.test(routeId || '');

    const [scenarioId, setScenarioId] = useState(isNewRoute || !routeValid ? null : Number(routeId));
    const [loadState, setLoadState] = useState(isNewRoute ? 'ready' : routeValid ? 'loading' : 'notFound');
    const [loadErrors, setLoadErrors] = useState(null);
    const [saved, setSaved] = useState(EMPTY);
    const [draft, setDraft] = useState(EMPTY);
    const [meta, setMeta] = useState(null);
    const [usage, setUsage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveErrors, setSaveErrors] = useState(null);
    const [modifiedOpen, setModifiedOpen] = useState(false);
    const [nameTouched, setNameTouched] = useState(false);

    const isNew = scenarioId === null;
    const canSave = isNew ? allowed('create') : allowed('update');
    const canRun = actionPermissions?.[RUN_ACTION] === 'allow';
    const needsReason = !isNew && modifiedReasonConfig.includes(NAME);

    const dirty = !sameDraft(draft, saved);
    const nameValid = isValidScenarioName(draft.name);
    const template = parseJsonObjectText(draft.inputTemplateText);
    const saveEnabled = canSave && loadState === 'ready' && !saving && nameValid && !template.error && (isNew || dirty);
    const inUse = (usage || []).length > 0;

    const guard = useUnsavedChangesGuard(dirty);

    const loadUsage = useCallback(
        async (id) => {
            const result = await scenariosServer.usage(id);
            setUsage(hasError(result) ? null : result.data || []);
        },
        [scenariosServer]
    );

    useEffect(() => {
        if (isNewRoute || !routeValid) return undefined;
        let cancelled = false;
        (async () => {
            const result = await scenariosServer.getById(routeId);
            if (cancelled) return;
            if (hasError(result)) {
                setLoadErrors(formatApiErrors(result));
                setLoadState('error');
                return;
            }
            const entity = result.data?.[0];
            if (!entity) {
                setLoadState('notFound');
                return;
            }
            const loaded = fromEntity(entity);
            setSaved(loaded);
            setDraft(loaded);
            setMeta({ modifiedBy: entity.modifiedBy, dateModified: entity.dateModified });
            setLoadState('ready');
            loadUsage(entity.id);
        })();
        return () => {
            cancelled = true;
        };
        // Loaded once per mount; the page remounts the editor for another id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = (field) => (value) => setDraft((current) => ({ ...current, [field]: value }));

    // Read by the preview at Run time, so typing here does not re-render it.
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const getSource = useCallback(
        () => ({ code: draftRef.current.code, inputTemplateText: draftRef.current.inputTemplateText }),
        []
    );

    // The code editor's breakpoints, paused line and recorded lines, for the
    // preview's Debug and Record.
    const debuggerRef = useRef(null);
    const mountCodeEditor = useCallback((editor, monaco) => {
        debuggerRef.current?.dispose();
        debuggerRef.current = createScenarioDebugger(editor, monaco);
    }, []);
    useEffect(
        () => () => {
            debuggerRef.current?.dispose();
            debuggerRef.current = null;
        },
        []
    );

    const save = async (modifiedReason) => {
        if (!saveEnabled) return;
        const sent = draft;
        const body = {
            name: sent.name,
            description: sent.description,
            code: sent.code,
            inputTemplate: template.value,
            isArchived: sent.isArchived
        };
        if (modifiedReason) body.modifiedReason = modifiedReason;

        setSaving(true);
        setSaveErrors(null);
        const result = isNew ? await scenariosServer.add(body) : await scenariosServer.update(scenarioId, body);
        setSaving(false);

        if (hasError(result)) {
            // TRIGGER_SCRAPER_SCENARIO_IN_USE and friends arrive here with the
            // database's own explanation; shown as it is.
            setSaveErrors(formatApiErrors(result));
            return;
        }

        // What was sent is now the saved state. Not the draft: anything typed
        // while the request was out is still unsaved.
        setSaved(sent);
        const entity = result.data || {};
        setMeta({ modifiedBy: entity.modifiedBy, dateModified: entity.dateModified });
        context.update({
            alertError: { message: i18n.t('scraperScenarios.saved'), type: 'snackbar', severity: 'success' }
        });

        if (isNew && entity.id) {
            setScenarioId(entity.id);
            onCreated(entity.id);
            // Past the guard: nothing is lost by moving to the scenario's own URL.
            guard.bypass(() => navigate(scenarioEditorPath(entity.id), { replace: true }));
        }
        if (entity.id) loadUsage(entity.id);
    };

    const requestSave = () => {
        if (!saveEnabled) return;
        if (needsReason) setModifiedOpen(true);
        else save();
    };

    const goBack = () => navigate(SCRAPER_SCENARIOS_PATH);

    // The header is a snapshot, installed again when what it shows changes. Its
    // buttons call through refs, so they always act on the current draft.
    const requestSaveRef = useRef(requestSave);
    requestSaveRef.current = requestSave;
    const goBackRef = useRef(goBack);
    goBackRef.current = goBack;
    const runRef = useRef(null);
    const shortcutsBlockedRef = useRef(false);
    shortcutsBlockedRef.current = modifiedOpen || guard.blocked || saving;

    const title = isNew
        ? i18n.t('pages.publicScraperScenarios.add')
        : saved.name || i18n.t('pages.publicScraperScenarios.edit');
    const trail = useMemo(() => findMenuTrail(config.menu, SCRAPER_SCENARIOS_PATH), []);
    const saveShortcut = isAppleDevice() ? '⌘S' : 'Ctrl+S';

    useEffect(() => {
        setHeaderActions?.(
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexGrow: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                    {trail.length > 0 && (
                        <Breadcrumbs
                            separator="/"
                            sx={{ 'typography': 'caption', 'color': 'text.secondary', '& .MuiBreadcrumbs-separator': { mx: 0.75 } }}
                        >
                            {trail.map((item) => (
                                <span key={item.path || item.title}>{i18n.t(item.title)}</span>
                            ))}
                        </Breadcrumbs>
                    )}
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography variant="h5" color="text.primary" noWrap>
                            {title}
                        </Typography>
                        {dirty && (
                            <Typography variant="body2" color="warning.main" noWrap>
                                {i18n.t('scraperScenarios.unsaved')}
                            </Typography>
                        )}
                    </Stack>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Button
                        color="inherit"
                        startIcon={<Iconify icon="eva:arrow-back-outline" />}
                        onClick={() => goBackRef.current()}
                    >
                        {i18n.t('scraperScenarios.back')}
                    </Button>
                    {canSave && (
                        <Tooltip title={saveShortcut}>
                            <span>
                                <LoadingButton
                                    variant="contained"
                                    loading={saving}
                                    disabled={!saveEnabled}
                                    startIcon={<Iconify icon="eva:save-outline" />}
                                    loadingPosition="start"
                                    onClick={() => requestSaveRef.current()}
                                >
                                    {i18n.t('buttons.save')}
                                </LoadingButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>
        );
    }, [setHeaderActions, trail, title, dirty, canSave, saving, saveEnabled, saveShortcut]);

    useEffect(() => () => clearHeaderActions?.(), [clearHeaderActions]);

    // Ctrl/Cmd+S saves, Ctrl/Cmd+Enter runs the preview — from anywhere on the
    // page, Monaco included. Capture phase, so the keys are taken before Monaco
    // sees them (its own Ctrl+Enter inserts a line) and before the browser's
    // Save Page dialog.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
            const key = event.key.toLowerCase();
            if (key !== 's' && key !== 'enter') return;
            event.preventDefault();
            event.stopPropagation();
            if (shortcutsBlockedRef.current) return;
            if (key === 's') requestSaveRef.current();
            else runRef.current?.();
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, []);

    if (loadState !== 'ready') {
        return (
            <Container maxWidth={false}>
                <Helmet>
                    <title>{i18n.t('pages.publicScraperScenarios.title')}</title>
                </Helmet>
                <Card sx={{ p: 3 }}>
                    {loadState === 'loading' && (
                        <Stack alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress />
                        </Stack>
                    )}
                    {loadState === 'notFound' && <Alert severity="warning">{i18n.t('scraperScenarios.notFound')}</Alert>}
                    {loadState === 'error' && (
                        <Alert severity="error">
                            {(loadErrors || []).map((line, index) => (
                                <div key={`${index}-${line}`}>{line}</div>
                            ))}
                        </Alert>
                    )}
                </Card>
            </Container>
        );
    }

    const templateError = template.error ? i18n.t(TEMPLATE_ERRORS[template.error]) : null;
    const nameChanged = !isNew && draft.name !== saved.name;
    const archiving = draft.isArchived && !saved.isArchived;

    let nameHelper = i18n.t('scraperScenarios.nameHelper');
    if (inUse && nameChanged) nameHelper = i18n.t('scraperScenarios.renameInUse');

    return (
        <Container
            maxWidth={false}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                // Fills the window on a wide screen, so each editor scrolls inside
                // itself; on a narrow one the panes stack and the page scrolls.
                height: { md: `calc(100vh - ${MAIN_CONTENT_OFFSET}px)` }
            }}
        >
            <Helmet>
                <title>{title}</title>
            </Helmet>

            <Card sx={{ p: 2, flexShrink: 0, overflow: 'visible' }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            label={i18n.t('columns.name')}
                            value={draft.name}
                            disabled={!canSave}
                            onChange={(event) => update('name')(event.target.value)}
                            onBlur={() => setNameTouched(true)}
                            error={(nameTouched || !isNew) && !nameValid}
                            helperText={nameHelper}
                            FormHelperTextProps={{ sx: { color: inUse && nameChanged ? 'warning.main' : undefined } }}
                            InputProps={{ sx: { fontFamily: 'monospace' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label={i18n.t('columns.description')}
                            value={draft.description}
                            disabled={!canSave}
                            onChange={(event) => update('description')(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={draft.isArchived}
                                    disabled={!canSave}
                                    onChange={(event) => update('isArchived')(event.target.checked)}
                                />
                            }
                            label={i18n.t('columns.isArchived')}
                        />
                    </Grid>
                </Grid>

                {!isNew && (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            {i18n.t('scraperScenarios.usedBy')}
                        </Typography>
                        {usage === null && (
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t('scraperScenarios.usageUnknown')}
                            </Typography>
                        )}
                        {usage !== null && usage.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t('scraperScenarios.usageNone')}
                            </Typography>
                        )}
                        {(usage || []).map((item) => (
                            <Chip
                                key={`${item.settingsId}-${item.action}`}
                                size="small"
                                variant="outlined"
                                icon={<Iconify icon="eva:link-2-outline" width={16} />}
                                label={`${item.settingsName} · ${item.action} · ${
                                    item.linkedBy === 'id'
                                        ? i18n.t('scraperScenarios.linkedById')
                                        : i18n.t('scraperScenarios.linkedByName')
                                }`}
                            />
                        ))}
                    </Stack>
                )}

                {inUse && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        {archiving
                            ? i18n.t('scraperScenarios.archiveInUse')
                            : i18n.t('scraperScenarios.usageWarning', { n: usage.length })}
                    </Alert>
                )}

                {saveErrors && (
                    <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setSaveErrors(null)}>
                        <Stack component="span" spacing={0.5}>
                            {saveErrors.map((line, index) => (
                                <Typography key={`${index}-${line}`} component="span" variant="body2">
                                    {line}
                                </Typography>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {meta?.dateModified && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                        {i18n.t('scraperScenarios.lastModified', {
                            // As the grid's DateTime cell shows it.
                            date: fDateTime(meta.dateModified, 'd MMM yyyy HH:mm'),
                            who: [meta.modifiedBy?.firstName, meta.modifiedBy?.lastName].filter(Boolean).join(' ') || '—'
                        })}
                    </Typography>
                )}
            </Card>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2
                }}
            >
                <Card
                    sx={{
                        flex: { md: '1 1 55%' },
                        minWidth: 0,
                        minHeight: { xs: 640, md: 0 },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        p: 2
                    }}
                >
                    <ScenarioDebuggerStyles />
                    <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="subtitle2">{i18n.t('scraperScenarios.code')}</Typography>
                        {canRun && (
                            <Typography variant="caption" color="text.secondary">
                                {i18n.t('scraperScenarios.breakpointsHelper')}
                            </Typography>
                        )}
                    </Stack>
                    <CodeEditor
                        language="javascript"
                        path={`scraper-scenario-${editorKey}.js`}
                        value={draft.code}
                        onChange={update('code')}
                        beforeMount={installScenarioSandbox}
                        onMount={mountCodeEditor}
                        options={DEBUGGER_EDITOR_OPTIONS}
                        readOnly={!canSave}
                        ariaLabel={i18n.t('scraperScenarios.code')}
                        sx={{ flex: 1 }}
                    />
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ pt: 1 }}>
                        <Typography variant="subtitle2">{i18n.t('scraperScenarios.inputTemplate')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('scraperScenarios.inputTemplateHelper')}
                        </Typography>
                    </Stack>
                    <CodeEditor
                        language="json"
                        path={`scraper-scenario-template-${editorKey}.json`}
                        value={draft.inputTemplateText}
                        onChange={update('inputTemplateText')}
                        readOnly={!canSave}
                        ariaLabel={i18n.t('scraperScenarios.inputTemplate')}
                        sx={{ height: 150, flexShrink: 0 }}
                    />
                    {templateError && (
                        <Typography variant="caption" color="error">
                            {templateError}
                        </Typography>
                    )}
                </Card>

                <Card sx={{ flex: { md: '1 1 45%' }, minWidth: 0, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ px: 2, pt: 2 }}>
                        {i18n.t('scraperScenarios.preview')}
                    </Typography>
                    <ScenarioPreviewPanel
                        server={scenariosServer}
                        settingsServer={server.publicGatewaySettings}
                        scenarioId={scenarioId}
                        editorKey={editorKey}
                        getSource={getSource}
                        canRun={canRun}
                        canReadSettings={allowed('read', 'publicGatewaySettings')}
                        runRef={runRef}
                        debuggerRef={debuggerRef}
                        proxyListsServer={server.publicProxyLists}
                        proxiesServer={server.publicProxies}
                        canReadProxyLists={allowed('read', 'publicProxyLists')}
                        canReadProxies={allowed('read', 'publicProxies')}
                    />
                </Card>
            </Box>

            <ModifiedDialog
                open={modifiedOpen}
                onCancel={() => setModifiedOpen(false)}
                onSubmit={async (reason) => {
                    await save(reason);
                    setModifiedOpen(false);
                }}
            />

            {guard.blocked && (
                // Mounted only while needed: ConfirmDialog reads its callbacks once,
                // in its constructor.
                <ConfirmDialog
                    dialogOpen
                    alertTitle={i18n.t('dialogs.confirmationTitle')}
                    alertText={i18n.t('dialogs.discardChanges')}
                    onConfirm={guard.proceed}
                    onCancel={guard.cancel}
                />
            )}
        </Container>
    );
}

ScenarioEditor.propTypes = {
    routeId: PropTypes.string,
    editorKey: PropTypes.number.isRequired,
    context: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    setHeaderActions: PropTypes.func,
    clearHeaderActions: PropTypes.func,
    onCreated: PropTypes.func.isRequired
};

/**
 * Route component for /app/scraper-scenarios/:id, `new` for a scenario not yet
 * created.
 *
 * The editor loads its scenario once, when it mounts, and is remounted for a
 * different id. Except one: creating a scenario moves the URL from `new` to its
 * id, and that is the same editor carrying on — remounting it would reload what
 * is already on screen and cut off a preview run in progress. The editor reports
 * the new id before it navigates, and that one change of id keeps the mount.
 */
export default function ScraperScenarioEditorPage({ params, context, navigate, setHeaderActions, clearHeaderActions }) {
    const routeId = params?.id;
    const createdRef = useRef(null);
    const [mount, setMount] = useState({ routeId, key: 0 });

    if (mount.routeId !== routeId) {
        const carriedOn = createdRef.current !== null && createdRef.current === routeId;
        createdRef.current = null;
        setMount({ routeId, key: carriedOn ? mount.key : mount.key + 1 });
    }

    const handleCreated = useCallback((id) => {
        createdRef.current = String(id);
    }, []);

    return (
        <ScenarioEditor
            key={mount.key}
            routeId={mount.routeId}
            editorKey={mount.key}
            context={context}
            navigate={navigate}
            setHeaderActions={setHeaderActions}
            clearHeaderActions={clearHeaderActions}
            onCreated={handleCreated}
        />
    );
}

ScraperScenarioEditorPage.propTypes = {
    params: PropTypes.object,
    context: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    setHeaderActions: PropTypes.func,
    clearHeaderActions: PropTypes.func
};
